package dk.smark.chat.crypto

import kotlinx.coroutines.await
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Uint8Array
import web.crypto.crypto
import kotlin.js.Promise

actual fun createCryptoProvider(): CryptoProvider = WebCryptoProvider()

class WebCryptoProvider : CryptoProvider {

    private val subtle = crypto.subtle.asDynamic()

    // ── Key generation ──────────────────────────────────────────────────────

    override suspend fun generateEcdhKeyPair(): EcKeyPair {
        val pair = (subtle.generateKey(ecdhKeyGenParams(), true, deriveKeyDeriveBitsUsages())
            as Promise<dynamic>).await()
        return exportKeyPair(pair)
    }

    override suspend fun generateEcdsaKeyPair(): EcKeyPair {
        val pair = (subtle.generateKey(ecdsaKeyGenParams(), true, signVerifyUsages())
            as Promise<dynamic>).await()
        return exportKeyPair(pair)
    }

    private suspend fun exportKeyPair(pair: dynamic): EcKeyPair {
        val pub = (subtle.exportKey("spki", pair.publicKey) as Promise<ArrayBuffer>).await()
        val priv = (subtle.exportKey("pkcs8", pair.privateKey) as Promise<ArrayBuffer>).await()
        return EcKeyPair(pub.toByteArray(), priv.toByteArray())
    }

    // ── ECDH ────────────────────────────────────────────────────────────────

    override suspend fun ecdhDeriveBits(privateKey: ByteArray, publicKey: ByteArray): ByteArray {
        val privKey = (subtle.importKey(
            "pkcs8", privateKey.toArrayBuffer(), ecdhKeyGenParams(), false, deriveBitsOnlyUsage()
        ) as Promise<dynamic>).await()

        val pubKey = (subtle.importKey(
            "spki", publicKey.toArrayBuffer(), ecdhKeyGenParams(), false, js("([])")
        ) as Promise<dynamic>).await()

        val bits = (subtle.deriveBits(ecdhDeriveParams(pubKey), privKey, 256)
            as Promise<ArrayBuffer>).await()

        return bits.toByteArray()
    }

    // ── ECDSA ───────────────────────────────────────────────────────────────

    override suspend fun ecdsaSign(privateKey: ByteArray, data: ByteArray): ByteArray {
        val privKey = (subtle.importKey(
            "pkcs8", privateKey.toArrayBuffer(), ecdsaKeyGenParams(), false, signOnlyUsage()
        ) as Promise<dynamic>).await()

        val sig = (subtle.sign(ecdsaSignParams(), privKey, data.toArrayBuffer())
            as Promise<ArrayBuffer>).await()

        return sig.toByteArray()
    }

    override suspend fun ecdsaVerify(
        publicKey: ByteArray,
        data: ByteArray,
        signature: ByteArray
    ): Boolean {
        val pubKey = (subtle.importKey(
            "spki", publicKey.toArrayBuffer(), ecdsaKeyGenParams(), false, verifyOnlyUsage()
        ) as Promise<dynamic>).await()

        return (subtle.verify(ecdsaSignParams(), pubKey, signature.toArrayBuffer(), data.toArrayBuffer())
            as Promise<Boolean>).await()
    }

    // ── HKDF ────────────────────────────────────────────────────────────────

    override suspend fun hkdf(
        inputKeyMaterial: ByteArray,
        salt: ByteArray,
        info: ByteArray,
        outputLength: Int
    ): ByteArray {
        val ikmKey = (subtle.importKey(
            "raw", inputKeyMaterial.toArrayBuffer(), hkdfAlgoName, false, deriveBitsOnlyUsage()
        ) as Promise<dynamic>).await()

        val bits = (subtle.deriveBits(
            hkdfParams(salt.toArrayBuffer(), info.toArrayBuffer()),
            ikmKey,
            outputLength * 8
        ) as Promise<ArrayBuffer>).await()

        return bits.toByteArray()
    }

    // ── AES-GCM ─────────────────────────────────────────────────────────────

    override suspend fun aesGcmEncrypt(key: ByteArray, plaintext: ByteArray, iv: ByteArray): ByteArray {
        val cryptoKey = importAesKey(key, encryptOnlyUsage())
        val result = (subtle.encrypt(aesGcmParams(iv.toArrayBuffer()), cryptoKey, plaintext.toArrayBuffer())
            as Promise<ArrayBuffer>).await()
        return result.toByteArray()
    }

    override suspend fun aesGcmDecrypt(key: ByteArray, ciphertext: ByteArray, iv: ByteArray): ByteArray {
        val cryptoKey = importAesKey(key, decryptOnlyUsage())
        val result = (subtle.decrypt(aesGcmParams(iv.toArrayBuffer()), cryptoKey, ciphertext.toArrayBuffer())
            as Promise<ArrayBuffer>).await()
        return result.toByteArray()
    }

    private suspend fun importAesKey(key: ByteArray, usages: dynamic): dynamic =
        (subtle.importKey("raw", key.toArrayBuffer(), aesKeyImportParams(), false, usages)
            as Promise<dynamic>).await()

    // ── Randomness ──────────────────────────────────────────────────────────

    override fun randomBytes(length: Int): ByteArray {
        val buf = Uint8Array(length)
        crypto.asDynamic().getRandomValues(buf)
        return buf.unsafeCast<ByteArray>()
    }
}
